<template>
    <div id="chatWindow" :key="renderCount">
        <v-tour :callbacks="tourCallbacks" :steps="steps" name="chat-window-tour">
            <template slot-scope="tour">
                <transition name="fade">
                    <v-step
                        v-for="(step, index) of tour.steps"
                        v-if="tour.currentStep === index"
                        :key="index"
                        :is-first="tour.isFirst"
                        :is-last="tour.isLast"
                        :labels="tour.labels"
                        :next-step="tour.nextStep"
                        :previous-step="tour.previousStep"
                        :step="step"
                        :stop="tour.stop"
                    >
                        <div slot="actions"></div>
                    </v-step>
                </transition>
            </template>
        </v-tour>
        <b-row v-if="activeConversation">
            <b-col :lg="layout && !observing.includes(activeConversation._id) ? 6 : 8" class="pb-3" md="8">
                <div class="chatContainer">
                    <div id="chatCloseSettingsButton">
                        <div class="user-meta">
                            <div v-if="activeConversation.fullname">
                                <div class="d-flex align-items-center">
                                    <a-avatar
                                        :key="activeConversation._id"
                                        :size="24"
                                        :src="getVisitorAvatarSrc()"
                                        shape="square"
                                        style="background-color: #3899ff; border-radius: 7px !important"
                                    >
                                        {{ activeConversation.fullname.substr(0, 1).toLocaleUpperCase() }}
                                    </a-avatar>
                                    <span
                                        ref="visitorNameInput"
                                        v-tippy="{
                                            followCursor: true,
                                            placement: 'bottom-end',
                                            theme: isDarkMode ? 'light-border' : 'google',
                                        }"
                                        :class="{ active: visitorNameInputActive }"
                                        :content="activeConversation.fullname"
                                        class="darkSpan ml-1 visitorName disableShortcut"
                                        contentEditable="true"
                                        @blur="setVisitorNameInputActive(false)"
                                        @focus="setVisitorNameInputActive(true)"
                                        @focusUserInput="focusVisitorNameInput()"
                                        @keydown="handleVisitorNameInputKeyDown"
                                        >{{ capitalize(activeConversation.fullname) }}
                                        <ActionEdit
                                            v-tippy="{
                                                followCursor: true,
                                                placement: 'bottom-end',
                                                theme: isDarkMode ? 'light-border' : 'google',
                                            }"
                                            :content="$t('message.clickToEdit')"
                                            class="mr-3"
                                            height="11"
                                            width="11"
                                        />
                                    </span>
                                    <span class="ml-2 d-flex align-items-center" style="color: #3899ff">
                                        <a-icon class="mr-2" type="mail" />
                                        <div
                                            ref="visitorEmailInput"
                                            v-tippy="{
                                                followCursor: true,
                                                placement: 'bottom-end',
                                                theme: isDarkMode ? 'light-border' : 'google',
                                            }"
                                            :class="{ active: visitorEmailInputActive }"
                                            :content="activeConversation.email"
                                            class="visitorEmail disableShortcut"
                                            contentEditable="true"
                                            @blur="setVisitorEmailInputActive(false)"
                                            @focus="setVisitorEmailInputActive(true)"
                                            @focusUserInput="focusVisitorEmailInput()"
                                            @keydown="handleVisitorEmailInputKeyDown"
                                        >
                                            {{
                                                activeConversation.email === ""
                                                    ? $t("message.addEmail")
                                                    : activeConversation.email
                                            }}
                                            <ActionEdit
                                                v-tippy="{
                                                    followCursor: true,
                                                    placement: 'bottom-end',
                                                    theme: isDarkMode ? 'light-border' : 'google',
                                                }"
                                                :content="$t('message.clickToEdit')"
                                                class="mr-3"
                                                height="11"
                                                width="11"
                                            />
                                        </div>
                                    </span>
                                </div>
                            </div>
                            <div v-else>
                                <a-avatar :size="24" shape="square">
                                    {{
                                        getCustomLoginInputField(activeConversation.customLoginInfos, "fullname")
                                            .substr(0, 1)
                                            .toLocaleUpperCase()
                                    }}
                                </a-avatar>

                                <span class="ellipsis">{{
                                    getCustomLoginInputField(activeConversation.customLoginInfos, "fullname")
                                }}</span>
                                <span
                                    >&lt;{{
                                        getCustomLoginInputField(activeConversation.customLoginInfos, "email")
                                    }}&gt;</span
                                >
                            </div>
                        </div>
                        <div class="d-flex flex-row" style="align-items: center">
                            <a-popover
                                v-if="!videoCallMode && !activeConversation.whatsappNumber"
                                id="call-button"
                                placement="bottomRight"
                                trigger="click"
                            >
                                <template slot="content">
                                    <div v-if="user">
                                        <button
                                            :disabled="videoCallMode"
                                            class="d-flex flex-row mt-3 align-items-center naked-btn"
                                            @click.prevent="call(false)"
                                        >
                                            <VideoCallSvg style="fill: #3899ff" />
                                            <div class="justify-content-center pl-3 popoverFont cursorPointer">
                                                Video Görüşmesi
                                            </div>
                                        </button>
                                        <button
                                            :disabled="videoCallMode"
                                            class="d-flex flex-row mt-3 align-items-center naked-btn"
                                            @click.prevent="call(true)"
                                        >
                                            <Call style="fill: #3899ff" />
                                            <div class="justify-content-center pl-3 popoverFont cursorPointer">
                                                Sesli Görüşme
                                            </div>
                                        </button>
                                    </div>
                                </template>
                                <button class="video-call-action-button">
                                    <VideoCallSvg style="fill: #3899ff" />
                                </button>
                            </a-popover>
                            <button v-else class="video-call-action-button" @click="cancelCall()">
                                <VideoCallCloseSvg style="fill: #f54e60" />
                            </button>
                            <a-popover
                                id="actionSettingsButton"
                                data-v-step="17"
                                placement="bottomRight"
                                title=""
                                trigger="click"
                            >
                                <template slot="content">
                                    <div v-if="user">
                                        <div class="d-flex flex-row">
                                            <div class="justify-content-start">
                                                <block />
                                            </div>
                                            <div
                                                v-if="
                                                    isRoot ||
                                                    user.role.permissions.map((x) => x.name).includes('visitor.block')
                                                "
                                                class="justify-content-end pl-3 popoverFont cursorPointer"
                                                @click.prevent="ban"
                                            >
                                                {{ $t("message.block_") }}
                                            </div>
                                        </div>
                                        <div class="d-flex flex-row mt-3">
                                            <div class="justify-content-start">
                                                <private-message-icon />
                                            </div>
                                            <div
                                                class="justify-content-end pl-3 popoverFont cursorPointer"
                                                @click.prevent="pm"
                                            >
                                                {{ $t("message.privateMessage_") }}
                                            </div>
                                        </div>
                                        <div
                                            v-if="observing.includes(activeConversation._id)"
                                            class="d-flex flex-row mt-3"
                                        >
                                            <div class="justify-content-start">
                                                <assign-representative />
                                            </div>
                                            <div
                                                v-b-modal.assign-agent
                                                class="justify-content-end pl-3 popoverFont cursorPointer"
                                            >
                                                {{ $t("message.assignRep") }}
                                            </div>
                                        </div>
                                        <div
                                            v-if="observing.includes(activeConversation._id)"
                                            class="d-flex flex-row mt-3"
                                        >
                                            <div class="justify-content-start">
                                                <open-chat />
                                            </div>
                                            <div
                                                class="justify-content-end pl-3 popoverFont cursorPointer"
                                                @click="joinTheChat(activeConversation)"
                                            >
                                                {{ $t("message.joinChat") }}
                                            </div>
                                        </div>
                                        <div
                                            v-if="!observing.includes(activeConversation._id)"
                                            class="d-flex flex-row mt-3"
                                        >
                                            <div class="justify-content-start">
                                                <forward-representative />
                                            </div>
                                            <div
                                                v-b-modal.forward-agent
                                                class="justify-content-end pl-3 popoverFont cursorPointer"
                                            >
                                                {{ $t("message.forwardRep") }}
                                            </div>
                                        </div>
                                        <div
                                            v-if="activeConversation.users.filter((u) => u !== null).length === 1"
                                            class="d-flex flex-row mt-3"
                                        >
                                            <div class="justify-content-start">
                                                <font-awesome-icon class="text-danger" icon="sign-out-alt" />
                                            </div>
                                            <div
                                                class="justify-content-end pl-3 popoverFont cursorPointer"
                                                @click.prevent="leave"
                                            >
                                                {{ $t("message.leaveChat") }}
                                            </div>
                                        </div>
                                        <div
                                            v-else-if="activeConversation.users.filter((u) => u !== null).length >= 2"
                                            class="d-flex flex-row mt-3"
                                        >
                                            <div class="justify-content-start">
                                                <font-awesome-icon class="text-danger" icon="sign-out-alt" />
                                            </div>
                                            <div
                                                class="justify-content-end pl-3 popoverFont cursorPointer"
                                                @click.prevent="end"
                                            >
                                                {{ $t("message.endChat") }}
                                            </div>
                                        </div>
                                        <div class="d-flex flex-row mt-3">
                                            <div class="justify-content-start">
                                                <font-awesome-icon icon="globe" />
                                            </div>
                                            <div class="justify-content-end pl-3 popoverFont cursorPointer">
                                                {{ $t("message.autoTranslate") }} :
                                                <a-switch v-model="isSwitched" class="ml-2 mr-2" size="small" />
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <button class="video-call-action-button">
                                    <chat-window-settings></chat-window-settings>
                                </button>
                            </a-popover>
                            <button class="video-call-action-button" data-v-step="18" @click.prevent="hide">
                                <minimize-window />
                            </button>
                            <button
                                v-if="
                                    !observing.includes(activeConversation._id) &&
                                    activeConversation.status !== 'destroyed'
                                "
                                v-tippy="{
                                    followCursor: true,
                                    placement: 'bottom-end',
                                    theme: isDarkMode ? 'light-border' : 'google',
                                }"
                                :content="
                                    activeConversation.users.filter((u) => u !== null).length === 1
                                        ? $t('message.endConversation')
                                        : $t('message.leaveChat')
                                "
                                class="video-call-action-button"
                                data-v-step="19"
                                @click.prevent="closeButton"
                            >
                                <power-off></power-off>
                            </button>
                            <button
                                v-if="
                                    observing.includes(activeConversation._id) &&
                                    activeConversation.status !== 'destroyed'
                                "
                                :disabled="!(activeConversation.status !== 'destroyed')"
                                class="video-call-action-button"
                                data-v-step="closeButton"
                                @click.prevent="leaveConversation(activeConversation._id)"
                            >
                                <circle-close-button />
                            </button>
                        </div>
                    </div>
                    <div
                        v-if="
                            !autoTranslate.includes(activeConversation._id) &&
                            user.lang !== activeConversation.visitor.preferredLang &&
                            !activeConversation.whatsappNumber
                        "
                        class="translate-detected"
                        @click="isSwitched = true"
                    >
                        {{
                            $t("message.autoTranslateDetectedDesc", {
                                visitorLanguage: !!languages.find(
                                    (x) => x.code === activeConversation.visitor.preferredLang
                                )
                                    ? languages.find((x) => x.code === activeConversation.visitor.preferredLang)
                                          .originalName
                                    : activeConversation.visitor.preferredLang,
                            })
                        }}
                    </div>
                    <div
                        :style="{
                            height: observing.includes(activeConversation._id)
                                ? 'calc(100% - 50px)'
                                : 'calc(100% - 169px)',
                        }"
                        class="messageList"
                        style="position: relative"
                    >
                        <MessageList
                            :always-scroll-to-bottom="true"
                            :colors="colors"
                            :is-colored="true"
                            :ischatwindow="true"
                            :messages="getMessages()"
                            :participants="getParticipants()"
                            :scroll-top="false"
                        />
                    </div>
                    <div :class="{ live: typingMessage !== '' }" class="message typing">
                        <div v-if="typingMessage !== ''" class="content p-0">
                            <div class="text" style="padding: 0 1rem">
                                <span
                                    v-tippy="{
                                        followCursor: true,
                                        placement: 'top-center',
                                        theme: isDarkMode ? 'light-border' : 'google',
                                    }"
                                    :content="typingMessage"
                                    class="quote white-space-pre-line"
                                >
                                    {{ activeConversation.fullname }} {{ $t("message.typing") }}: {{ typingMessage }}...
                                </span>
                            </div>
                        </div>
                    </div>

                    <form
                        v-if="!observing.includes(activeConversation._id) && activeConversation.status !== 'destroyed'"
                        id="frmChat"
                        autocomplete="off"
                        class="d-flex justify-content-between align-items-center bg-white"
                        style="position: relative; margin-top: auto; height: 117px; overflow: hidden"
                        @keydown="catchShortcut"
                        @submit.prevent="sendMessage"
                    >
                        <md-button
                            style="position: absolute; top: 0; right: 74px; margin: 0; width: 24px; min-width: 24px"
                            @click="selectFile"
                        >
                            <md-icon>attachment</md-icon>
                        </md-button>
                        <md-button
                            id="send-message-button"
                            style="position: absolute; top: 0; right: 30px; margin: 0; width: 24px; min-width: 24px"
                            tabindex="1"
                            type="submit"
                            variant="none"
                        >
                            <send-icon></send-icon>
                        </md-button>
                        <VoiceRecorder
                            v-if="
                                !observing.includes(activeConversation._id) && activeConversation.status !== 'destroyed'
                            "
                            :cb="sendVoiceMessage"
                        />

                        <ckeditor
                            id="chat-editor"
                            :key="'ckeditor-' + $i18n.locale"
                            :ref="`ckeditor-${activeConversation._id}`"
                            v-model="messageModel"
                            :config="editorConfig()"
                            :editor="editor"
                            class="border-0 w-100"
                            tag-name="textarea"
                            @ready="onEditorReady"
                            @enter.native="enter"
                            @keydown.native="handleSubmit"
                        />
                        <div
                            v-if="waitingTabShortcut && suggestions.length > 0"
                            id="suggestions"
                            ref="toolbarChat"
                            autofocus
                            @blur="blurSCpanel"
                            @keydown="catchShortcut"
                        >
                            <ul>
                                <li
                                    v-for="(subContent, index) in suggestions"
                                    :key="subContent._id"
                                    :class="{
                                        active: index === suggestionsCurrent,
                                    }"
                                    :data-id="subContent._id"
                                    :data-letter="subContent.letter"
                                    :data-number="subContent.number"
                                    class="pointer tab"
                                    @click="onShortcutSelected(subContent.languageContent[$i18n.locale].content)"
                                >
                                    {{ subContent.languageContent[$i18n.locale].name }}
                                    <span class="pointer-label">{{ subContent.letter }}+{{ subContent.number }}</span>
                                    <p class="d-none" v-html="subContent.languageContent[$i18n.locale].content" />
                                </li>
                            </ul>
                        </div>
                    </form>
                </div>
                <div
                    v-if="shortcutReminder"
                    class="d-flex justify-content-start align-items-center mt-1 shortcut-tippy"
                >
                    <span class="material-icons" style="color: #efa445">lightbulb</span>
                    <strong>{{ shortcut.shortcutKey }}</strong>
                    <span class="ml-1">{{ shortcut[$i18n.locale] }}</span>
                </div>
            </b-col>
            <b-col
                v-if="layout && !observing.includes(activeConversation._id)"
                class="pl-1.5 pb-3"
                data-v-step="20"
                lg="3"
                md="4"
            >
                <DefaultContensWidget :onShortcutSelected="onShortcutSelected" />
            </b-col>
            <b-col
                :lg="layout && !observing.includes(activeConversation._id) ? 3 : 4"
                :md="layout ? 6 : 4"
                class="pl-1.5 pb-3"
                data-v-step="21"
            >
                <UserInfoWidget />
            </b-col>
        </b-row>
        <UserChatHistoryWidget v-if="isChatHistoryOpen" />
    </div>
</template>

<script>
import "../utils/request";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import moment from "moment";
import "vue-wysiwyg/dist/vueWysiwyg.css";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import CKEditor from "@ckeditor/ckeditor5-vue";
import UserInput from "./popup-chat/UserInput";
import getRandomShortcut from "../utils/shortcutAll";
import { capitalize, fakeConversation, isMac, validateEmail } from "../utils/utils";
import languages from "../language/languages.json";

import {
    faBan,
    faBoxOpen,
    faCopy,
    faGlobe,
    faHeadset,
    faInfo,
    faPaperPlane,
    faPowerOff,
    faShare,
    faSignOutAlt,
    faTimes,
    faUser,
    faUserSecret,
    faWindowMinimize,
} from "@fortawesome/free-solid-svg-icons";
import Info from "./svg/Info";
import Representative from "./svg/Representative";
import SendIcon from "./svg/SendIcon";
import ChatId from "./svg/ChatId";
import Operating from "./svg/Operating";
import World from "./svg/World";
import Ip from "./svg/Ip";
import User from "./svg/User";
import Mail from "./svg/Mail";
import VideoCallSvg from "./svg/VideoCallSvg";
import KeywordShortCut from "./svg/KeywordShortCut";
import ConversationColor from "./svg/ConversationColor";
import PowerOff from "./svg/PowerOff";
import ChatWindowSettings from "./svg/ChatWindowSettings";
import HideChat from "./svg/HideChat";
import Block from "./svg/Block";
import ShowIntro from "./svg/ShowIntro";
import CircleCloseButton from "./svg/CircleCloseButton";
import AssignRepresentative from "./svg/AssignRepresentative";
import ForwardRepresentative from "./svg/ForwardRepresentative";
import PrivateMessageIcon from "./svg/PrivateMessageIcon";
import MinimizeWindow from "./svg/MinimizeWindow";
import PrivateChatRepresentative from "./PrivateChatRepresentative";
import ConversationColorHelp from "./ConversationColorHelp";
import KeyboardShortcuts from "./KeyboardShortcuts";
import UserInfoWidget from "./UserInfoWidget";
import DefaultContensWidget from "./DefultContentsWidget";
import UserChatHistoryWidget from "./UserChatHistoryWidget";
import MessageList from "./popup-chat/MessageList";
import * as _ from "lodash";
import { log2 } from "../utils/log2";
import { sendSocketMessage, socketMessageStream } from "../utils/socketStream";
import { filter, map } from "rxjs/operators";
import { createChatWindowTourSteps } from "../utils/chatWindowTourSteps";
import Cookies from "js-cookie";
import { publishMessage, systemMessageStream } from "../utils/systemPubSub";
import { toNewMessagesModel } from "../utils/chat";
import { paragraphStyleExtender } from "./util/paragraphStyleExtender";
import PrivateMessage from "./PrivateMessage";
import ForgotPassword from "../views/Pages/ForgotPassword";
import { PHONE } from "../webrtc/phoneSDK";
import VideoCallScreen from "./VideoCallScreen";
import { PHONE_UTILS } from "../utils/phone";
import { uploadAsset } from "../api/assetUpload";
import axios from "axios";
import VoiceRecorder from "./VoiceRecorder";
import mimeTypes from "./util/mimeTypes";
import Call from "./svg/Call";
import VideoCallCloseSvg from "./svg/VideoCallCloseSvg";
import OpenChat from "./svg/OpenChat";
import CONFIG from "../config/config";
import { getPersistenceData } from "../utils/persistenceStorage";
import ActionEdit from "./svg/ActionEdit";

library.add(
    faUser,
    faPowerOff,
    faPaperPlane,
    faInfo,
    faWindowMinimize,
    faCopy,
    faBan,
    faTimes,
    faBoxOpen,
    faUserSecret,
    faSignOutAlt,
    faShare,
    faHeadset,
    faGlobe
);
const log = log2("ChatWindow");
log("CKEditor", { CKEditor, ClassicEditor });

export default {
    name: "ChatWindow",
    components: {
        VoiceRecorder,
        ForwardRepresentative,
        ForgotPassword,
        PrivateMessage,
        Block,
        UserChatHistoryWidget,
        DefaultContensWidget,
        UserInfoWidget,
        User,
        Mail,
        Ip,
        World,
        Operating,
        ChatId,
        SendIcon,
        Representative,
        Info,
        KeywordShortCut,
        ConversationColor,
        ConversationColorHelp,
        KeyboardShortcuts,
        MessageList,
        CKEditor,
        UserInput,
        PowerOff,
        ChatWindowSettings,
        HideChat,
        PrivateChatRepresentative,
        AssignRepresentative,
        PrivateMessageIcon,
        MinimizeWindow,
        CircleCloseButton,
        ShowIntro,
        VideoCallScreen,
        VideoCallSvg,
        VideoCallCloseSvg,
        Call,
        OpenChat,
        ActionEdit,
    },
    component: {
        Info,
        Representative,
        SendIcon,
        ChatId,
        Operating,
        World,
    },
    data() {
        return {
            languages,
            renderCount: 0,
            visitorNameInputActive: false,
            visitorEmailInputActive: false,
            tourCallbacks: {
                onPreviousStep: this.onTourPrev,
                onNextStep: this.onTourNext,
                onStart: this.onTourStart,
                onStop: this.onTourStop,
            },
            steps: createChatWindowTourSteps(this),
            keydownBinded: false,
            message: "",
            markedBannedMessage: "",
            messages: [],
            pmMessage: "",
            suggestionsCurrent: 0,
            bannedWordDetected: false,
            oldConversations: [],
            waitingTabShortcut: false,
            suggestions: [],
            typingMessage: "",
            lastChar: "",
            cancelTokens: [],
            editor: ClassicEditor,
            editorConfig: () => ({
                // The configuration of the editor.
                toolbar: ["bold", "italic", "link"],
                placeholder: this.$t("message.typeHere"),
                extraPlugins: [],
                startupFocus: true,
                removePlugins: ["MediaEmbed", "AutoMediaEmbed"],
            }),
            wysiwyg: {
                paragraphSeparator: "br",
                maxHeight: "100px",
                hideModules: {
                    justifyLeft: true,
                    justifyCenter: true,
                    justifyRight: true,
                    headings: true,
                    code: true,
                    orderedList: true,
                    unorderedList: true,
                    image: true,
                    table: true,
                    removeFormat: true,
                    separator: true,
                },
            },
            chatColorModalVisible: false,
            keyboardShortCutsModalVisible: false,
            colors: {
                header: {
                    bg: "#4e8cff",
                    text: "#ffffff",
                },
                launcher: {
                    bg: "#4e8cff",
                },
                messageList: {
                    bg: "#ffffff",
                },
                sentMessage: {
                    bg: "#4e8cff",
                    text: "#ffffff",
                },
                receivedMessage: {
                    bg: "#eaeaea",
                    text: "#222222",
                },
                userInput: {
                    bg: "#f4f7f9",
                    text: "#565867",
                },
            },
            wideScreen: false,
            layout: false,
            shortcut: getRandomShortcut(),
            callingSound: new Audio("./static/media/videoCallSounds/incoming-call.ogg"),
            coolingTime: Date.now(),
        };
    },
    beforeDestroy() {
        this.messages = [];
        this.oldConversations = [];
        this.cancelTokens.forEach((cancel) => cancel());
        this.callingSound.pause();
    },
    mounted() {
        log("ChatWindow mounted", {
            isRoot: this.isRoot,
            permissions: this.user.role.permissions.map((x) => x.name),
            isInclude: this.user.role.permissions.map((x) => x.name).includes("visitor.block"),
        });
        if (this.activeConversation) {
            this.loadOldMessages(this.activeConversation._id).then((response) => {
                this.messages = [];
                for (let data of response.data.data) {
                    this.addMessage(data);
                }
            });

            this.loadOldConversations(this.activeConversation.visitor).then((response) => {
                this.oldConversations = response.data;
            });

            this.getContents({
                perPage: 100,
                page: 1,
            }).then(() => {
                this.layout = true; //this.contents.count && this.contents.data[0].children.length
            });
            this.getSettings().then();
            document.querySelector("#chatWindow").focus();

            const showChatIntroSub = systemMessageStream
                .pipe(filter((x) => x.type === "showChatIntro"))
                .subscribe((x) => {
                    this.$tours["chat-window-tour"].start();
                });
            const enterSub = systemMessageStream.pipe(filter((x) => x.type === "chat.enterPress")).subscribe((x) => {
                this.sendMessage();
            });
            this.cancelTokens.push(() => showChatIntroSub.unsubscribe());
            this.cancelTokens.push(() => enterSub.unsubscribe());
            this.bannedWordClearTimerId = setInterval(() => {
                if (this.bannedWordDetected && Date.now() - this.lastMessageUpdateTS > 500) {
                    this.lastCheckedBannedMessage = this.markBannedWords(this.message);
                    if (!this.lastCheckedBannedMessage.includes('class="banned"')) {
                        this.bannedWordDetected = false;
                        this.message = this.lastCheckedBannedMessage;
                    }
                }
            }, 500);
            this.cancelTokens.push(() => clearInterval(this.bannedWordClearTimerId));
        } else {
            log.error("active chat is not setted");
        }
        let translateSub = systemMessageStream
            .pipe(
                filter((x) => x.type === "translate_message"),
                map((x) => x.payload)
            )
            .subscribe((x) => {
                this.messages = this.messages.map((msg) => {
                    if (msg.uniqMessageId === x.uniqMessageId) {
                        msg.translates = [...msg.translates, ...x.translates];
                    }
                    return msg;
                });
            });
        this.cancelTokens.push(() => translateSub.unsubscribe());
    },
    computed: {
        ...mapState({
            autoTranslate: (state) => state.chat.autoTranslate,
            language: (state) => state.language.language,
            user: (state) => state.auth.user,
            shortcutReminder: (state) => state.app.shortcutReminder,
            isRoot: (state) => state.auth.user.role.isRoot,
            observing: (state) => state.chat.observing,
            contents: (state) => state.content.contents,
            activeConversation: (state) =>
                state.app.fakeMode ? state.chat.fakeActiveConversation : state.chat.activeConversation,
            settings: (state) => state.setting.settings,
            site: (state) => state.site.site,
            bannedWords: (state) => state.bannedWord.bannedWords.data.filter((f) => f.isActive).map((x) => x.content),
            isChatHistoryOpen: (state) => state.chat.isChatHistoryOpen,
            CustomLoginInputs: (state) =>
                state.customLoginForm.inputs.reduce((acc, cur) => {
                    acc[cur._id] = cur;
                    return acc;
                }, {}),
            videoCallMode: (state) => state.webrtc.videoCallMode,
            calling: (state) => state.webrtc.calling,
            soundOnly: (state) => state.webrtc.soundOnly,
        }),
        ...mapGetters({
            getActiveConversations: "chat/getActiveConversations",
            getUser: "user/getUser",
            grouped: "content/getGrouped",
            isDarkMode: "user/isDarkMode",
            isCalling: "webrtc/isCalling",
        }),
        isSwitched: {
            get() {
                return this.autoTranslate.includes(this.activeConversation._id);
            },
            set(val) {
                this.setAutoTranslate({
                    id: this.activeConversation._id,
                    state: val,
                });
                if (this.autoTranslate.includes(this.activeConversation._id)) {
                    this.$message.success({
                        content: `${this.$t("message.autoTranslate")} ${this.$t("message.on")}`,
                        duration: 3,
                        key: "translate",
                    });
                } else {
                    this.$message.warning({
                        content: `${this.$t("message.autoTranslate")} ${this.$t("message.off")}`,
                        duration: 3,
                        key: "translate",
                    });
                }
            },
        },

        filteredMessages() {
            return this.messages;
        },
        messageModel: {
            get() {
                return this.message;
            },
            set(value) {
                let msg = value;
                /*let msg = this.markBannedWords(value)*/
                msg = msg.replaceAll("&#8203;", "");
                msg = this.trimNewLine(msg);
                this.lastMessageUpdateTS = Date.now();
                this.message = msg;
            },
        },
    },
    methods: {
        ...mapActions({
            addObservation: "chat/addObservation",
            getContents: "content/getContents",
            endConversation: "chat/endConversation",
            leaveConversation: "chat/leaveConversation",
            hideConversation: "chat/hideConversation",
            banConversation: "chat/banConversation",
            loadOldConversations: "chat/loadOldConversations",
            loadOldMessages: "chat/loadOldMessages",
            getSettings: "setting/getSettings",
            setPrivateMessageConversation: "chat/setPrivateMessageConversation",
            setShortcutReminder: "app/setShortcutReminder",
            setVideoCallMode: "webrtc/setVideoCallMode",
            setCalling: "webrtc/setCalling",
            viewFakeConversation: "chat/viewFakeConversation",
            multiBeepConversation: "chat/multiBeepConversation",
            joinConversation: "chat/joinConversation",
        }),
        ...mapMutations({
            setAutoTranslate: "chat/SET_AUTO_TRANSLATE",
        }),
        getVisitorAvatarSrc() {
            if (this.activeConversation.whatsappNumber) {
                return `${CONFIG.WHATSAPP.avatarAddress}?contactId=${this.activeConversation.whatsappNumber}&siteId=${
                    this.site._id
                }&token=${getPersistenceData("Admin-Token")}&r=${Math.random() * 1000}`;
            }
            return undefined;
        },
        shortItem(text) {
            if (text.length > 20) {
                return text.substring(0, 20) + "...";
            } else {
                return text;
            }
        },
        capitalize,
        setVisitorNameInputActive(active) {
            log("setVisitorNameInputActive", active);
            this.visitorNameInputActive = active;
            if (!active) {
                this.focusCkEditor();
                let fullname = this.$refs.visitorNameInput.textContent.toString();
                log("fullname before", "" + fullname);
                fullname = (fullname || "")
                    .trim()
                    .toLowerCase()
                    .split(" ")
                    .map((x) => x.trim())
                    .filter((x) => x.length > 0)
                    .map((x) => capitalize(x))
                    .join(" ");
                log("fullname after", fullname);
                const c = this.activeConversation;
                if (!fullname.length) {
                    this.renderCount += 1;
                    return;
                }
                if (c && c.fullname !== fullname) {
                    const visitorId = typeof c.visitor === "object" ? c.visitor._id : c.visitor;
                    log("fullname updating", fullname);
                    sendSocketMessage("user visitor-info-updated", {
                        siteId: this.site._id,
                        conversationId: this.activeConversation._id,
                        visitorId,
                        fullname,
                        email: c.email,
                    });
                    this.$toasted.show(this.$t("message.visitorNameUpdated"), {
                        duration: 3000,
                        position: "top-center",
                    });
                } else {
                    this.renderCount += 1;
                }
            }
        },
        joinTheChat(conversation, minimized = false, autoAssign = false) {
            if (this.fakeMode) {
                this.viewFakeConversation(fakeConversation());
                return;
            }
            this.multiBeepConversation([conversation._id]);
            this.joinConversation({
                conversation,
                observe: false,
                minimized: minimized,
                autoAssign: autoAssign,
                sendToSocket: true,
            });
        },
        focusVisitorNameInput() {
            if (this.$nextTick) {
                this.$nextTick(() => {
                    if (this.$refs.visitorNameInput) this.$refs.visitorNameInput.focus();
                });
            }
        },
        handleVisitorNameInputKeyDown(event) {
            if (event.keyCode === 13 && !event.shiftKey) {
                this.$refs.visitorNameInput.blur();
                event.preventDefault();
            } else if (event.keyCode === 27) {
                event.preventDefault();
            }
        },
        setVisitorEmailInputActive(active) {
            this.visitorEmailInputActive = active;
            if (!active) {
                this.focusCkEditor();
                let email = this.$refs.visitorEmailInput.textContent.toString();
                email = email.toLowerCase().trim();
                if (validateEmail(email)) {
                    const c = this.activeConversation;

                    if (c && c.email !== email) {
                        const visitorId = typeof c.visitor === "object" ? c.visitor._id : c.visitor;
                        log("email updating", email);
                        sendSocketMessage("user visitor-info-updated", {
                            siteId: this.site._id,
                            conversationId: this.activeConversation._id,
                            visitorId,
                            fullname: c.fullname,
                            email,
                        });
                        this.$toasted.show(this.$t("message.visitorEmailUpdated"), {
                            duration: 3000,
                            position: "top-center",
                        });
                    }
                } else {
                    this.$toasted.error(this.$t("message.visitorEmailIsNotValid"), {
                        duration: 3000,
                        position: "top-center",
                    });
                }
                this.renderCount += 1;
            }
        },
        focusVisitorEmailInput() {
            if (this.$nextTick) {
                this.$nextTick(() => {
                    if (this.$refs.visitorEmailInput) {
                        this.$refs.visitorEmailInput.focus();
                        const c = this.activeConversation;
                        if (c.email === "") {
                            this.$refs.visitorEmailInput.innerHTML = "";
                        }
                    }
                });
            }
        },
        handleVisitorEmailInputKeyDown(event) {
            if (event.keyCode === 13 && !event.shiftKey) {
                this.$refs.visitorEmailInput.blur();
                event.preventDefault();
            } else if (event.keyCode === 27) {
                event.preventDefault();
            }
        },
        selectFile() {
            let fileEl = document.createElement("input");
            fileEl.setAttribute("type", "file");
            fileEl.onchange = async () => {
                let file = fileEl.files[0];
                let path = `${this.site._id}/file/${this.activeConversation._id}`;

                let { data } = await uploadAsset({
                    path,
                    mime: file.type,
                    ext: mimeTypes[file.type] || null,
                });
                log("file", { file, data });
                if (file.size <= 10 * 1024 * 1024) {
                    await axios({
                        method: data.method,
                        url: data.url,
                        data: file,
                        headers: {
                            "Content-Type": file.type,
                        },
                    });
                    sendSocketMessage("user media message", {
                        conversationId: this.activeConversation._id,
                        userId: this.user._id,
                        username: this.user.name,
                        siteId: this.site._id,
                        asset: data.asset,
                        whatsappNumber: this.activeConversation.whatsappNumber,
                    });
                } else {
                    log.warn("input is not suitable for send");
                    this.$toasted.error(this.$t("message.assetOversize"), {
                        duration: 3000,
                        position: "bottom-center",
                    });
                }
            };
            fileEl.click();
        },
        getCustomLoginInputField(customLoginObject = [], desiredInfoTypeStr) {
            let inputValues = customLoginObject
                .filter((x) => this.CustomLoginInputs[x.input])
                .map((v) => ({
                    value: v.value,
                    name: this.CustomLoginInputs[v.input].type,
                }));
            const input = inputValues.find((x) => x.name === desiredInfoTypeStr);
            return _.get(input, "value", "-");
        },
        onEditorReady(editor) {
            log.debug("onEditorReady");
            window.ckeditor = editor;
            paragraphStyleExtender(editor);
            /*  editor.model.change(writer => {
writer.setSelection(editor.model.document.getRoot(), 'end');
});*/
            editor.editing.view.focus();

            this.ckeditor = editor;
        },
        onTourPrev(currentStep) {
            log.debug("tour prev", currentStep);
        },
        onTourNext(currentStep) {
            log.debug(
                "tour next",
                currentStep,
                this.$tours["chat-window-tour"].steps[currentStep + 1].target,
                this.$tours["chat-window-tour"]
            );
            /*if(this.$tours["chat-window-tour"].steps[currentStep+1].target === '[data-v-step="hideAction"]'){
$('#ellipsisButton').trigger("click")
this.$forceUpdate()
}*/
        },
        onTourStart() {
            log.debug("tour start");
            if (
                this.activeConversation.status === "destroyed" ||
                this.observing.includes(this.activeConversation._id)
            ) {
                _.remove(this.steps, function (n) {
                    return (
                        n.target === '[data-v-step="closeButton2"]' ||
                        n.target === '[data-v-step="preparedContentWidget"]'
                    );
                });
                log("Unnecessary tour steps removed for observing mode and destroyed conversations", this.steps);
            }
        },
        onTourStop() {
            log.debug("tour stop");
            Cookies.set("chat_tour_viewed", "true");
        },

        showTour() {
            log("this.$tours['chat-window-tour']", this.$tours["chat-window-tour"]);
            this.$tours["chat-window-tour"].start();
            publishMessage("showChatIntro", {});
        },
        skipTour() {
            Cookies.set("chat_tour_viewed", "true");
        },
        getParticipants() {
            const supportUsers = this.messages
                .filter((x) => x.messageType === "user" || x.messageType === "private")
                .map((x) => ({
                    id: x.userId,
                    name: x.username,
                }));

            return _.uniqBy(supportUsers, (q) => q.name);
        },
        getMessages() {
            let allMessages = toNewMessagesModel(this.messages, this);

            if (this.typingMessage) {
                allMessages = [
                    ...allMessages,
                    {
                        type: "typing",
                        data: {
                            text: this.typingMessage,
                        },
                    },
                ];
            }
            /*log("allMessages", allMessages, this.typingMessage);*/
            return allMessages;
        },
        toggleChatColorModal(visible) {
            log("toggleChatColorModal called", visible);
            this.chatColorModalVisible = visible;
        },
        toggleKeyboardShotcutModal(visible) {
            log("toggleKeyboardShotcutModal called", visible);
            this.keyboardShortCutsModalVisible = visible;
        },

        ban() {
            this.$dialog
                .confirm(this.$t("message.blockUserConfirm"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.banConversation(this.activeConversation._id);
                });
        },
        pm() {
            this.setPrivateMessageConversation(this.activeConversation._id);
        },
        leave() {
            this.$dialog
                .confirm(this.$t("message.leaveChartConfirm"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    const isValid =
                        this.activeConversation.status === "destroyed" ||
                        this.observing.includes(this.activeConversation._id) ||
                        this.activeConversation.users.filter((u) => u !== null).length === 1;
                    if (isValid) {
                        this.leaveConversation(this.activeConversation._id);
                    } else {
                        this.$toasted.error(this.$t("message.onlyOneRep"), {
                            duration: 3000,
                        });
                    }
                });
        },
        end() {
            this.$dialog
                .confirm(this.$t("message.endChatConfirm"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.stopVC();
                    this.endConversation(this.activeConversation._id);
                });
        },
        hide() {
            this.hideConversation();
        },
        call(soundOnly = false, screenOnly = false) {
            if (this.calling) {
                log.warn("video call request is not allowed , when another call request waiting");
                return;
            }
            log("call running");
            let config = {
                userId: this.user._id,
                secure: true,
                ssl: true,
                autocam: true,
            };
            if (!screenOnly) {
                let media = {
                    video: soundOnly ? false : { facingMode: "user" },
                    audio: true,
                };
                config = { media, ...config };
            }
            const phone = PHONE_UTILS.getPhone() || PHONE(config);
            phone.configUpdate(config);
            log("call running", { phone, soundOnly });

            PHONE_UTILS.setPhoneInstance(phone);
            phone.startcamera();
            const c = this.activeConversation;
            const visitorId = typeof c.visitor === "object" ? c.visitor._id : c.visitor;
            this.setVideoCallMode({
                videoCallMode: true,
                conversationId: c._id,
                visitorId,
                soundOnly,
            });
            this.setCalling(true);
            sendSocketMessage("user video-call-request", {
                siteId: this.site._id,
                userId: this.user._id,
                visitorId: this.activeConversation.visitor,
                conversationId: this.activeConversation._id,
                soundOnly,
            });
        },
        sendTypingInfo: _.debounce(function () {
            if (this.message.length) {
                if (!!this.activeConversation)
                    sendSocketMessage("user typing message", {
                        conversationId: this.activeConversation._id,
                        userId: this.user._id,
                        username: this.user.name,
                        message: "rep-typing",
                        siteId: this.site._id,
                        whatsappNumber: _.get(this.activeConversation, "whatsappNumber", null),
                    });
            }
        }, 500),
        sendMessage() {
            log("sendMessage before", {
                pm: this.pmMessage.trim(),
                message: this.message.trim(),
            });
            //enter pm setTen önce çalışabiliyor
            const regex = /(<([^>]+)>)/gi;

            if (
                _.take(this.message.replaceAll(`<p>&nbsp;</p>`, "").replace(regex, ""), 10).join("").startsWith("pm:")
            ) {
                this.pmMessage = this.pmMessage = this.message.trim().replace("pm:", "");
            }

            if (this.pmMessage.trim().length) {
                sendSocketMessage("user private-message", {
                    conversationId: this.activeConversation._id,
                    userId: this.user._id,
                    username: this.user.name,
                    message: this.pmMessage,
                    siteId: this.site._id,
                });
                this.pmMessage = "";
                this.message = "";
                setTimeout(() => {
                    this.getCKInstance().ui.editor.setData("");
                    this.message = "";
                }, 50);
            } else {
                if (this.message.trim().length) {
                    let clearedMessage = this.message
                        .trim()
                        .replace(/↵/g, "\n")
                        .replace(/^(\s+<br( \/)?>)*|(<br( \/)?>\s)*$/gm, "")
                        .replace(/<a (.*)>/g, '<a target="_blank" $1>');
                    let bannedMarkedMessage = this.markBannedWords(clearedMessage);
                    if (bannedMarkedMessage.includes("class='banned'")) {
                        this.message = bannedMarkedMessage;
                        log.warn("banned message detected", bannedMarkedMessage);
                        if (!this.bannedWordDetected)
                            this.$toasted.error("Yasaklı kelime tespit edildi, mesajı düzeltip gönderin lütfen", {
                                duration: 2000,
                                position: "bottom-center",
                            });
                        this.bannedWordDetected = true;
                    } else {
                        if (this.bannedWordDetected) {
                            this.bannedWordDetected = false;
                            this.message = bannedMarkedMessage;
                            this.sendMessage();
                            return;
                        }
                        log("clearedMessage", clearedMessage);
                        for (const m of ["@name", "@email"]) {
                            let val = m === "@name" ? this.activeConversation.fullname : this.activeConversation.email;
                            clearedMessage = clearedMessage.replace(new RegExp(m, "g"), val);
                        }
                        console.log("clearedMessage", clearedMessage);
                        sendSocketMessage("user message", {
                            conversationId: this.activeConversation._id,
                            userId: this.user._id,
                            username: this.user.name,
                            message: clearedMessage,
                            siteId: this.site._id,
                            whatsappNumber: this.activeConversation.whatsappNumber,
                        });
                        this.pmMessage = "";
                        this.message = "";
                        setTimeout(() => {
                            this.getCKInstance().ui.editor.setData("");
                            this.message = "";
                        }, 50);
                    }
                }
            }
        },
        async sendVoiceMessage(blob) {
            let path = `${this.site._id}/voice/${this.activeConversation._id}`;
            const mimeType = "audio/mpeg";
            const ext = "mp3";
            let { data } = await uploadAsset({ path, mime: mimeType, ext });
            await axios({
                method: data.method,
                url: data.url,
                data: blob,
                // headers: { "Content-Type": "application/octet-stream" },
                headers: { "Content-Type": mimeType },
            });
            sendSocketMessage("user media message", {
                conversationId: this.activeConversation._id,
                userId: this.user._id,
                username: this.user.name,
                siteId: this.site._id,
                asset: data.asset,
                whatsappNumber: this.activeConversation.whatsappNumber,
            });
            log("sendVoiceMessage", { blob, data });
        },
        enter(e) {
            log("enter", e);
        },
        trimNewLine(msg = "") {
            msg = msg.trim();
            while (msg.startsWith("<p>&nbsp;</p>")) {
                msg = msg.replace("<p>&nbsp;</p>", "");
            }
            while (msg.startsWith('<p><br data-cke-filler="true"></p>')) {
                msg = msg.replace('<p><br data-cke-filler="true"></p>', "");
            }
            while (msg.endsWith("<p>&nbsp;</p>")) {
                msg = msg.substring(0, msg.lastIndexOf("<p>&nbsp;</p>"));
            }
            return msg;
        },
        handleSubmit(e) {
            /*log("handleSubmit", e.domEvent)*/
            if ("Enter" === e.domEvent.key && !e.domEvent.shiftKey) {
                if (this.waitingTabShortcut) {
                    e.domEvent.preventDefault();
                    log("waitingTabShortcut state", {
                        suggestions: this.suggestions,
                        suggestionsCurrent: this.suggestionsCurrent,
                        msg: this.suggestions[this.suggestionsCurrent].languageContent[this.$i18n.locale].content,
                    });
                    this.message = this.suggestions[this.suggestionsCurrent].languageContent[this.$i18n.locale].content;
                    this.blurSCpanel();
                    log("editor", this.getCKInstance().ui.editor);
                    /*const ck = this.$refs[`ckeditor-${this.activeConversation._id}`].instance.ui.editor
ck.model.createPositionAt(ck.model.document.selection.getLastPosition())*/
                } else {
                    log("handleSubmit", e, e.domEvent.key, this.getCKInstance());
                    e.domEvent.preventDefault();

                    this.message = this.getCKInstance().ui.editor.getData("");
                    this.$nextTick(() => this.sendMessage());
                    this.waitingTabShortcut = false;
                    this.suggestionsCurrent = 0;
                }
            }
        },
        catchShortcut(e) {
            let shortcut = false;
            if (this.waitingTabShortcut && e.key.toLowerCase() !== "arrowup" && e.key.toLowerCase() !== "arrowdown") {
                shortcut = document.querySelector(
                    `div.tab[data-letter="${this.waitingTabShortcut}"][data-number="${e.key}"]`
                );

                if (!shortcut) {
                    log("waitingTabShortcut false 1", {
                        shortcut,
                        key: e.key,
                        qSelector: document.querySelector(
                            `div.tab[data-letter="${this.waitingTabShortcut}"][data-number="${e.key}"]`
                        ),
                    });
                    this.waitingTabShortcut = false;
                    this.suggestionsCurrent = 0;
                }
            } else {
                if (e.key && (isMac ? e.ctrlKey || e.metaKey : e.altKey)) {
                    shortcut = document.querySelector(`div.alt[data-letter="${e.key}"]`);
                } else if (e.key.toLowerCase() === "tab") {
                    const elem = document.getElementsByClassName("ck-content")[0];
                    const message = elem ? elem.innerText : "";

                    const allWords = _.flatten(
                        message
                            .trim()
                            .split(" ")
                            .map((x) => x.split("\n"))
                    );
                    const lastWord = _.last(allWords);
                    const matchedItems = document.querySelector(`div.tab[data-letter="${lastWord}"]`);
                    log.debug("tab matchedItems", {
                        matchedItems,
                        message: this.message,
                        allWords,
                        lastWord,
                    });
                    if (matchedItems) {
                        this.waitingTabShortcut = lastWord;
                        this.suggestions = this.contents.data.filter((w) => w.letter === lastWord);
                    } else {
                        log("waitingTabShortcut false 2");
                        this.lastChar = "";
                        this.waitingTabShortcut = false;
                        this.suggestionsCurrent = 0;
                    }
                } else if (e.key.toLowerCase() === "arrowup" && this.waitingTabShortcut) {
                    log("waitingTabShortcut false 3 before", {
                        suggestionsCurrent: this.suggestionsCurrent,
                    });
                    const oldIndex = this.suggestionsCurrent;
                    if (this.suggestionsCurrent > 0) {
                        this.suggestionsCurrent--;
                        this.$refs.toolbarChat.scrollTop = this.suggestionsCurrent * 25;
                    }

                    this.message = this.suggestions[this.suggestionsCurrent].languageContent[this.$i18n.locale].content;
                    log("waitingTabShortcut false 3", {
                        suggestionsCurrent: this.suggestionsCurrent,
                    });
                    if (oldIndex === 0) {
                        this.waitingTabShortcut = false;
                        this.suggestionsCurrent = 0;
                    }
                } else if (e.key.toLowerCase() === "arrowdown" && this.waitingTabShortcut) {
                    if (this.suggestions.length - 1 > this.suggestionsCurrent) {
                        this.suggestionsCurrent++;
                        this.$refs.toolbarChat.scrollTop = this.suggestionsCurrent * 25;
                    }

                    this.message = this.suggestions[this.suggestionsCurrent].languageContent[this.$i18n.locale].content;
                }
            }

            if (shortcut) {
                log("shortcut", shortcut);
                e.preventDefault();
                const id = shortcut.getAttribute("data-id");
                const content = this.contents.data.find((x) => x._id === id);
                if (content) {
                    this.message = content.languageContent[this.$i18n.locale].content;
                    log("waitingTabShortcut false 4");
                    this.waitingTabShortcut = false;
                    this.suggestionsCurrent = 0;
                }

                /*const editorEl =document.getElementById('frmChat');*/

                return false;
            } else {
                this.lastChar = e.key;
                this.focusCkEditor();
            }
        },
        focusCkEditor() {
            setTimeout(
                function () {
                    if (this.activeConversation) {
                        const editorEl = document.getElementsByClassName("ck-editor__editable")[0];
                        if (editorEl) editorEl.focus();
                    }
                }.bind(this),
                10
            );
        },
        blurSCpanel() {
            log("blurSCpanel");
            this.waitingTabShortcut = false;
            this.suggestionsCurrent = 0;
        },
        onShortcutSelected(str) {
            log("onShortcutSelected called");
            this.message = str;
            this.waitingTabShortcut = false;
            this.suggestionsCurrent = 0;
            this.focusCkEditor();
        },
        addMessage(data) {
            log("addMessage", data);
            let items = [...this.messages, data];
            items = _.orderBy(
                _.uniqBy(items, (q) => q.uniqMessageId || q._id),
                (q) => moment(q.createdDate).toDate().getTime(),
                "asc"
            );
            this.messages = items;
        },
        closeButton() {
            if (this.activeConversation.users.length === 1 && !this.observing.includes(this.activeConversation._id)) {
                this.end();
            } else {
                this.leaveConversation(this.activeConversation._id);
            }
        },
        stopVC() {
            log("cancelVideoCall", this.dialingVisitorId);
            if (this.dialingVisitorId && this.dialingConversationId) {
                const phone = PHONE_UTILS.getPhone();
                sendSocketMessage("user video-call-close", {
                    siteId: this.site._id,
                    userId: this.user._id,
                    visitorId: this.dialingVisitorId,
                    conversationId: this.dialingConversationId,
                    isSoundOnly: this.soundOnly,
                });

                if (this.videoCallMode) {
                    phone.camera.stop();
                    phone.hangup(this.dialingVisitorId);
                }
                this.setVideoCallMode({
                    videoCallMode: false,
                    conversationId: null,
                    visitorId: null,
                    soundOnly: false,
                });
                this.setCalling(false);
            }
        },
        getCKInstance(id) {
            id = id ? id : this.activeConversation._id;
            try {
                const instance = this.$refs[`ckeditor-${id}`].instance || this.$refs[`ckeditor-${id}`].$_instance;
                return instance;
            } catch (e) {
                log.warn("ck instance not found", { id });
                return undefined;
            }
        },
        markBannedWords(msg) {
            if (!this.settings.bannedWordMarking) return msg;
            const clearRegex = /<span class="banned">(\w+)<\/span>/gm;
            const before = msg;
            msg = msg.replaceAll(clearRegex, (match, group) => {
                return group;
            });
            //log.debug("mesut: after",{before,after:msg})
            // msg = msg.replaceAll(/<\/span><\/span>/gm,'</span>')

            this.bannedWords.forEach((bannedWord) => {
                //gerizekalı pgerizekalı gerizekalıp mgerizekalımalt satır gerizekalı pgerizekalı
                bannedWord = bannedWord.trim().toLowerCase();
                const regex = new RegExp(`${bannedWord}`, "gmi");
                /*msg = msg.replaceAll(`<span class="banned">${bannedWord}</span>`, `${bannedWord}`)*/
                msg = msg.replaceAll(regex, (match, index) => {
                    const startIndex = index;
                    const endIndex = index + bannedWord.length - 1;
                    const leftChar = msg[startIndex - 1];
                    const rightChar = msg[endIndex + 1];
                    const isBanned =
                        [undefined, " ", ">", ";"].includes(leftChar) && [undefined, " ", "<", "&"].includes(rightChar);

                    if (isBanned) {
                        //log.debug("mesut: replaceAll",{startIndex,endIndex,leftChar,isBanned,match})
                        return `<span class="banned">${match}</span>`;
                    } else {
                        return match;
                    }
                });
                // msg = msg.replaceAll(regex, `<span class="banned">${bannedWord}</span>`)
            });
            //log.debug("mesut: markBannedWords after",msg)
            return msg;
        },

        onResize() {
            this.wideScreen = false;
            log("onresize");
        },
        collapsechat() {
            document.querySelector(".ck.ck-reset.ck-editor.ck-rounded-corners").classList.toggle("collapsed");
        },
        log(blob) {
            log("blob", blob);
        },
        cancelCall() {
            publishMessage("vc_ended", { signal: true });
        },
        translate(data) {
            if (
                ["visitor"].includes(data.messageType) &&
                data.baseLang !== this.user.lang &&
                !data.translates.find((x) => x.to === this.user.lang)
            ) {
                sendSocketMessage("user translate-message", {
                    siteId: this.site._id,
                    to: this.user.lang,
                    data,
                });
            }
        },
    },
    created() {
        const sub = socketMessageStream.pipe(filter((x) => ["message"].includes(x.cmd))).subscribe((x) => {
            const { data } = x;
            console.log("new message", { data, active: this.activeConversation._id });
            if (this.activeConversation && this.activeConversation._id !== data.conversationId) {
                log.warn("unrelated message received from different chat", {
                    currentChatId: this.activeConversation._id,
                    otherChatId: data.conversationId,
                });
                return;
            }
            if (data.type === "customer-typing") {
                if (this.settings.writing === 1) {
                    this.typingMessage = data.message;
                }
                return;
            }
            this.translate(data);
            this.addMessage(data);
            if (data.messageType === "visitor") {
                setTimeout(() => {
                    this.typingMessage = "";
                }, 500);
            }
        });
        this.cancelTokens.push(() => sub.unsubscribe());
    },
    updated() {
        if (this.$refs[`ckeditor-${this.activeConversation._id}`] && !this.keydownBinded) {
            setTimeout(() => {
                try {
                    const instance = this.getCKInstance();
                    instance.editing.view.document.on(
                        "keydown",
                        (event, data) => {
                            /*log("keydown received", event)*/
                            this.handleSubmit(data);
                        },
                        { priority: "high" }
                    );
                    this.keydownBinded = true;
                } catch (e) {
                    log.error("keydown bind error", e);
                    this.keydownBinded = false;
                }
            }, 100);
        }
        if (this.language.code !== this.$i18n.locale) {
            log("lang diff", this.language.code, this.$i18n.locale);
            this.$i18n.locale = this.language.code;
            this.steps = createChatWindowTourSteps(this);
        }
    },
    watch: {
        activeConversation: {
            handler(newValue, oldValue) {
                log("activeConversation changed");
                if (newValue._id !== oldValue._id) {
                    this.message = "";
                    this.typingMessage = "";
                    const oldCK = this.getCKInstance(oldValue._id);
                    const newCK = this.getCKInstance(newValue._id);
                    if (oldCK) oldCK.ui.editor.setData("");
                    if (newCK) newCK.ui.editor.setData("");
                    this.focusCkEditor();

                    this.loadOldMessages(newValue._id).then((response) => {
                        this.messages = [];
                        for (let data of response.data.data) {
                            this.addMessage(data);
                        }
                    });
                    this.keydownBinded = false;
                }
            },
        },
        language(newValue) {
            log("language changed");
            this.steps = createChatWindowTourSteps(this);
            this.$forceUpdate();
        },
        waitingTabShortcut(newValue) {
            log("waitingTabShortcut changed", newValue, {
                suggestionsCurrent: this.suggestionsCurrent,
            });
            this.waitingTabShortcut = newValue;
        },
        message(newValue) {
            log("message changed", newValue);
            if (newValue.includes("<span>")) {
                const regex = /<span>|<\/span>/gi;
                this.message = newValue.replace(regex, "");
                setTimeout(() => {
                    this.ckeditor.model.change((writer) => {
                        writer.setSelection(this.ckeditor.model.document.getRoot(), "end");
                    });
                }, 100);
            }
            if (this.settings.agentTyping) {
                this.sendTypingInfo();
            }
        },
        isCalling(newValue) {
            if (newValue) {
                if (typeof this.callingSound.loop == "boolean") {
                    this.callingSound.loop = true;
                } else {
                    this.callingSound.addEventListener(
                        "ended",
                        function () {
                            this.currentTime = 0;
                            this.play();
                            console.log("video call ended ", this);
                        },
                        false
                    );
                }
                this.callingSound.play();
            } else {
                this.callingSound.pause();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.new-theme {
    .ellipsis {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 200px;
    }

    .video-call-action-button {
        background: none;
        border: none;
        padding: 0;
        margin: 0 12px 0 0;
        outline: none;
    }

    #chatWindow {
        .row {
            margin: 0;
        }

        .chatContainer {
            border-radius: 6px;
            box-shadow: 0 0 3px #00000080;
            height: calc(100vh - 180px);
            overflow: hidden;
            position: relative;

            .messageList {
                height: calc(100% - 169px);
            }
        }
    }

    #chatWindowCloseHideButton {
        display: flex;
        justify-content: flex-end;
        padding-bottom: 2px;
    }

    #chatCloseSettingsButton {
        display: flex;
        justify-content: space-between;
        padding: 10px 10px 10px 10px;
        background-color: white;
        align-items: center;
        height: 30px;
    }

    .cursorPointer {
        cursor: pointer;
    }

    .cursorPointer:hover {
        font-weight: 600;
    }

    .popoverFont {
        font-family: Poppins;
        font-size: 14px;
        color: #181c32;
        font-weight: 300;
    }

    .text-danger {
        color: #afafaf !important;
    }
}

.dark-mode {
    .darkSpan {
        color: white !important;
    }

    .ant-avatar {
        background: #333440 !important;
    }
}

.translate-detected {
    padding: 0.6rem;
    z-index: 1;
    background: #ffe6c1;
    width: 100%;
    position: absolute;
    color: #ffa82a;
    cursor: pointer;
}
</style>
<style lang="scss">
.naked-btn {
    outline: none;
    border: none;
    background-color: transparent;
}

.message.typing {
    min-height: 20px;
    background: white;
    color: #1e1e2d;
    width: calc(100% - 4px);
}

.message.typing.live {
    background: #e4f8fe;
    -webkit-line-clamp: 1;
    text-overflow: ellipsis;
    max-height: 20px;
    word-break: break-all;
}
</style>
